// src/views/admin/inventory/component/DataTableFilter.jsx

import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { useForm, Controller } from 'react-hook-form';
import { getItemCategoryService } from '@/services/ItemCategoryServices';
import { getTaxService } from '@/services/TaxServices';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';

const DataTableFilter = ({ onChangeFilter }) => {
  /* ───────────────── refs for clearing ───────────────── */
  const selectCategoryRef = useRef(null);
  const selectTaxRef = useRef(null);
  const selectItemTypeRef = useRef(null);
  const selectIsFeaturedRef = useRef(null);
  const selectPerishableRef = useRef(null);
  const selectStatusRef = useRef(null);

  /* ───────────────── react-hook-form ───────────────── */
  const {
    setValue,
    getValues,
    control,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: '',
      price: '',
      item_category_id: '',
      tax_id: '',
      item_type: '',
      is_featured: '',
      status: '',
      isPerishable: '', // '' for All, '1' for Yes, '0' for No
    },
    mode: 'onSubmit',
  });

  const dispatch = useDispatch();

  /* ───────────────── dropdown data ───────────────── */
  const [taxesOptionList, setTaxesOptionList] = useState([]);
  const [itemCategoriesOptionList, setItemCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const statusOptionList = [
    { value: 1, label: 'Active' },
    { value: 2, label: 'Inactive' },
  ];
  const isFeaturedOptionList = [
    { value: 1, label: 'Yes' },
    { value: 0, label: 'No' },
  ];
  const itemTypeOptionList = [
    { value: 1, label: 'Veg' },
    { value: 2, label: 'Non Veg' },
  ];
  const perishableOptionList = [
    { value: '', label: 'All' },
    { value: '1', label: 'Yes' },
    { value: '0', label: 'No' },
  ];

  /* ───────────────── init fetch ───────────────── */
  useEffect(() => {
    fetchItemCategories();
    fetchTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───────────────── helpers ───────────────── */
  const clearSelection = () => {
    [selectCategoryRef, selectTaxRef, selectItemTypeRef, selectIsFeaturedRef, selectPerishableRef, selectStatusRef]
      .forEach((r) => {
        if (r.current && typeof r.current.clearValue === 'function') {
          r.current.clearValue();
        }
      });
  };

  const formReset = async () => {
    reset({
      name: '',
      price: '',
      item_category_id: '',
      tax_id: '',
      item_type: '',
      is_featured: '',
      status: '',
      isPerishable: '',
    });
    clearSelection();
    // Clear all filters
    onChangeFilter('extra_filter', {});
  };

  /* ───────────────── fetch remote options ───────────────── */
  const fetchTaxes = () => {
    setIsLoading(true);
    dispatch(getTaxService({ search: '', page: 1, limit: 100, extra_filter: {} }))
      .then((res) => {
        const arr = res?.data?.data?.data || [];
        setTaxesOptionList(arr.map((t) => ({ value: t._id, label: t.name })));
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  const fetchItemCategories = () => {
    setIsLoading(true);
    dispatch(getItemCategoryService({ search: '', page: 1, limit: 100, extra_filter: {} }))
      .then((res) => {
        const arr = res?.data?.data?.data || [];
        setItemCategories(
          arr.map((c) => ({
            value: c._id,
            label: c.name + (c.isPerishable ? ' 🍃' : ''),
            isPerishable: !!c.isPerishable,
          }))
        );
        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  /* ───────────────── submit ───────────────── */
  const onSubmit = (data) => {
    // Build extra_filter object
    const filterData = {};

    if (data.name && data.name.trim() !== '') {
      filterData.name = data.name.trim();
    }
    if (data.price && data.price.trim() !== '') {
      filterData.price = data.price.trim();
    }
    if (data.item_category_id) {
      filterData.item_category_id = data.item_category_id;
    }
    if (data.tax_id) {
      filterData.tax_id = data.tax_id;
    }
    if (data.item_type !== '') {
      // data.item_type may be number or string; ensure numeric
      const v = Number(data.item_type);
      if (!isNaN(v)) filterData.item_type = v;
    }
    if (data.is_featured !== '') {
      const v = Number(data.is_featured);
      if (!isNaN(v)) filterData.is_featured = v;
    }
    if (data.status !== '') {
      const v = Number(data.status);
      if (!isNaN(v)) filterData.status = v;
    }
    if (data.isPerishable !== '') {
      // Convert '1' to true, '0' to false
      filterData.isPerishable = data.isPerishable === '1';
    }
    // Pass the constructed extra_filter
    onChangeFilter('extra_filter', filterData);
  };

  /* ───────────────── render ───────────────── */
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-5 mb-5">
      <div className="row">
        {/* ---- Name ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Name"
                id="name"
                className="db-field-control"
              />
            )}
          />
        </div>

        {/* ---- Price ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="price"
            render={({ field }) => (
              <Input
                {...field}
                size="sm"
                labelText="Price"
                id="price"
                className="db-field-control"
              />
            )}
          />
        </div>

        {/* ---- Category ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="item_category_id"
            render={({ field }) => (
              <InputSelect
                ref={selectCategoryRef}
                id="item_category_id"
                size="sm"
                labelText="Category"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  itemCategoriesOptionList.find((d) => d.value === field.value) ||
                  null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('item_category_id', val);
                }}
                options={itemCategoriesOptionList}
              />
            )}
          />
        </div>

        {/* ---- Tax ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="tax_id"
            render={({ field }) => (
              <InputSelect
                ref={selectTaxRef}
                id="tax_id"
                size="sm"
                labelText="Tax"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  taxesOptionList.find((d) => d.value === field.value) || null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('tax_id', val);
                }}
                options={taxesOptionList}
              />
            )}
          />
        </div>

        {/* ---- Status ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <InputSelect
                ref={selectStatusRef}
                id="status"
                size="sm"
                labelText="Status"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  statusOptionList.find((d) => d.value === field.value) ||
                  null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('status', val);
                }}
                options={statusOptionList}
              />
            )}
          />
        </div>

        {/* ---- Item Type ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="item_type"
            render={({ field }) => (
              <InputSelect
                ref={selectItemTypeRef}
                id="item_type"
                size="sm"
                labelText="Item Type"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  itemTypeOptionList.find((d) => d.value === field.value) ||
                  null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('item_type', val);
                }}
                options={itemTypeOptionList}
              />
            )}
          />
        </div>

        {/* ---- Is Featured ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="is_featured"
            render={({ field }) => (
              <InputSelect
                ref={selectIsFeaturedRef}
                id="is_featured"
                size="sm"
                labelText="Is Featured"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  isFeaturedOptionList.find((d) => d.value === field.value) ||
                  null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('is_featured', val);
                }}
                options={isFeaturedOptionList}
              />
            )}
          />
        </div>

        {/* ---- Perishable ---- */}
        <div className="col-12 sm:col-6 md:col-4 xl:col-3">
          <Controller
            control={control}
            name="isPerishable"
            render={({ field }) => (
              <InputSelect
                ref={selectPerishableRef}
                id="isPerishable"
                size="sm"
                labelText="Perishable"
                labelStyleClassName="db-field-title after:hidden"
                value={
                  perishableOptionList.find((d) => d.value === field.value) || null
                }
                onChange={(option) => {
                  const val = option ? option.value : '';
                  setValue('isPerishable', val);
                }}
                options={perishableOptionList}
              />
            )}
          />
        </div>

        {/* ---- Buttons ---- */}
        <div className="col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="submit" className="db-btn py-2 text-white bg-primary">
              <i className="lab lab-search-line lab-font-size-16" />
              <span>Search</span>
            </button>
            <button
              type="button"
              onClick={formReset}
              className="db-btn py-2 text-white bg-gray-600"
            >
              <i className="lab lab-cross-line-2 lab-font-size-22" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default DataTableFilter;
